import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminShell } from '../features/admin/AdminShell';
import { PlansPage } from '../features/admin/PlansPage';
import { SuperLoginPage } from '../features/admin/SuperLoginPage';
import { TenantDetailPage } from '../features/admin/TenantDetailPage';
import { TenantsPage } from '../features/admin/TenantsPage';
import { LoginPage } from '../features/auth/LoginPage';
import { CustomersPage } from '../features/customers/CustomersPage';
import { DriverDetailPage } from '../features/drivers/DriverDetailPage';
import { DriversPage } from '../features/drivers/DriversPage';
import { HistoryPage } from '../features/history/HistoryPage';
import { LivePage } from '../features/live/LivePage';
import { PlacesPage } from '../features/places/PlacesPage';
import { CompanySettingsPage } from '../features/settings/CompanySettingsPage';
import { DevicesPage } from '../features/settings/DevicesPage';
import { RolesPage } from '../features/settings/RolesPage';
import { SettingsLayout } from '../features/settings/SettingsLayout';
import { UsersPage } from '../features/settings/UsersPage';
import { TripDetailPage } from '../features/trips/TripDetailPage';
import { TripFormPage } from '../features/trips/TripFormPage';
import { ExpensesPage } from '../features/expenses/ExpensesPage';
import { ExpenseDetailPage } from '../features/expenses/ExpenseDetailPage';
import { InvoicesPage } from '../features/invoices/InvoicesPage';
import { InvoiceFormPage } from '../features/invoices/InvoiceFormPage';
import { InvoiceDetailPage } from '../features/invoices/InvoiceDetailPage';
import { InvoicePrintPage } from '../features/invoices/InvoicePrintPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { MaintenancePage } from '../features/maintenance/MaintenancePage';
import { AlertsPage } from '../features/alerts/AlertsPage';
import { AlertRulesPage } from '../features/alerts/AlertRulesPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { TripsPage } from '../features/trips/TripsPage';
import { GroupsPage } from '../features/vehicles/GroupsPage';
import { VehicleDetailPage } from '../features/vehicles/VehicleDetailPage';
import { VehiclesPage } from '../features/vehicles/VehiclesPage';
import { LandingPage } from '../features/landing/LandingPage';
import { DevMapPage } from './DevMapPage';
import { RequireStaff, RequireSuper } from './guards';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { AppShell } from './shell/AppShell';

export const router = createBrowserRouter([
  { path: '/landing', element: <LandingPage />, errorElement: <RouteErrorBoundary /> },
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  ...(import.meta.env.DEV ? [{ path: '/dev/map', element: <DevMapPage /> }] : []),
  { path: '/admin/login', element: <SuperLoginPage />, errorElement: <RouteErrorBoundary /> },
  {
    element: <RequireSuper />,
    children: [
      {
        path: '/admin',
        element: <AdminShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <Navigate to="tenants" replace /> },
          { path: 'tenants', element: <TenantsPage /> },
          { path: 'tenants/:id', element: <TenantDetailPage /> },
          { path: 'plans', element: <PlansPage /> },
        ],
      },
    ],
  },
  {
    element: <RequireStaff />,
    children: [
      {
        path: '/',
        element: <AppShell />,
        errorElement: <RouteErrorBoundary />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'live', element: <LivePage /> },
          { path: 'history', element: <HistoryPage /> },
          { path: 'trips', element: <TripsPage /> },
          { path: 'trips/new', element: <TripFormPage /> },
          { path: 'trips/:id', element: <TripDetailPage /> },
          { path: 'trips/:id/edit', element: <TripFormPage /> },
          { path: 'vehicles', element: <VehiclesPage /> },
          { path: 'vehicles/groups', element: <GroupsPage /> },
          { path: 'vehicles/:id', element: <VehicleDetailPage /> },
          { path: 'drivers', element: <DriversPage /> },
          { path: 'drivers/:id', element: <DriverDetailPage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'customers/:id', element: <CustomersPage /> },
          { path: 'places', element: <PlacesPage /> },
          { path: 'expenses', element: <ExpensesPage /> },
          { path: 'expenses/:id', element: <ExpenseDetailPage /> },
          { path: 'maintenance', element: <MaintenancePage /> },
          { path: 'invoices', element: <InvoicesPage /> },
          { path: 'invoices/new', element: <InvoiceFormPage /> },
          { path: 'invoices/:id', element: <InvoiceDetailPage /> },
          { path: 'invoices/:id/edit', element: <InvoiceFormPage /> },
          { path: 'invoices/:id/print', element: <InvoicePrintPage /> },
          { path: 'reports', element: <ReportsPage /> },
          { path: 'alerts', element: <AlertsPage /> },
          { path: 'alerts/rules', element: <AlertRulesPage /> },
          { path: 'alerts/:id', element: <AlertsPage /> },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              { index: true, element: <Navigate to="company" replace /> },
              { path: 'company', element: <CompanySettingsPage /> },
              { path: 'users', element: <UsersPage /> },
              { path: 'roles', element: <RolesPage /> },
              { path: 'devices', element: <DevicesPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
